import React, { useEffect, useMemo, useRef, useState } from "react";

const SentencePracticeBlock = ({
  segment,
  segments,
  renderHelpPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  sentenceLabel,
  currentWord,
  handleWordPlay,
  openWordMenu,
  handleRangePlay, // <- add this prop from parent
}) => {
  
  const selectionAreaRef = useRef(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);

  const normalizedSelection = useMemo(() => {
    if (selectionStart == null || selectionEnd == null) return null;

    return {
      start: Math.min(selectionStart, selectionEnd),
      end: Math.max(selectionStart, selectionEnd),
    };
  }, [selectionStart, selectionEnd]);

  const clearSelection = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setShowSelectionMenu(false);
  };

  const playSelectedRange = () => {
    if (!normalizedSelection || !segment?.words?.length) return;

    const { start, end } = normalizedSelection;
   

    handleRangePlay?.({
  startIndex: start,
  endIndex: end,
});
  };

  const handleWordMouseDown = (index, e) => {
    if (practiceMode === "shadowing") return;

    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setShowSelectionMenu(false);
  };

  const handleWordMouseEnter = (index) => {
    if (!isSelecting) return;
    setSelectionEnd(index);
  };

  const handleWordMouseUp = () => {
  if (!isSelecting) return;

  setIsSelecting(false);

  if (
    selectionStart != null &&
    selectionEnd != null &&
    selectionStart !== selectionEnd
  ) {
    setShowSelectionMenu(true);
  }
};
useEffect(() => {
  clearSelection();
}, [segment?.id]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
  if (isSelecting) {
    setIsSelecting(false);

    if (
      selectionStart != null &&
      selectionEnd != null &&
      selectionStart !== selectionEnd
    ) {
      setShowSelectionMenu(true);
    }
  }
};
    const handleDocumentMouseDown = (e) => {
      if (!selectionAreaRef.current) return;

      if (!selectionAreaRef.current.contains(e.target)) {
        clearSelection();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg shadow-blue-500/5">
      <div className="px-3 sm:px-4 py-3">
        <div className="sm:hidden space-y-2">
          <div className="flex items-center">
            <div className="w-10" />

            <div className="flex-1 flex justify-center min-w-0">
              {renderHelpPill()}
            </div>

            <div className="w-10 flex justify-end">
              {isMetadataNoHelp && (
                <button
                  type="button"
                  onClick={() => setShowExerciseHelp(true)}
                  className="h-9 px-4 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 border border-slate-300/60 text-sm font-medium text-slate-700 backdrop-blur-sm transition-all duration-200 hover:shadow-sm"
                  title="About course"
                >
                  About
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handlePreviousSegment}
              disabled={segment.id === 0}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shrink-0",
                segment.id === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-lg leading-none">←</span>
              <span className="text-sm font-medium">Prev</span>
            </button>

            <div className="relative h-6 flex items-center justify-center">
              <div
                className={[
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
                  showCenterCue ? "opacity-0" : "opacity-100",
                ].join(" ")}
              >
                <span className="text-sm font-semibold text-gray-700">
                  Sentence {segment.id + 1}/{segments.length}
                </span>
              </div>

              <div
                className={[
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
                  showCenterCue ? "opacity-100" : "opacity-0",
                ].join(" ")}
              >
                {practiceMode === "echoing" && echoingPhase === "paused" && (
                  <span className="text-sm font-semibold text-blue-700">
                    Get ready…
                  </span>
                )}

                {practiceMode === "shadowing" &&
                  (isCueActive || recordingStatus === "recording") && (
                    <span
                      className={`text-sm font-semibold transition-all duration-200 ${
                        recordingStatus === "recording"
                          ? "text-green-600 scale-105"
                          : "text-amber-700"
                      }`}
                    >
                      {recordingStatus === "recording" ? "Go!" : "Get ready…"}
                    </span>
                  )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleNextSegment}
              disabled={segment.id >= segments.length - 1}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shrink-0",
                segment.id >= segments.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-sm font-medium">Next</span>
              <span className="text-lg leading-none">→</span>
            </button>
          </div>
        </div>

        <div className="hidden sm:flex flex-col gap-2">
          <div className="flex items-center min-h-[24px]">
            <div className="flex-1" />

            <div className="flex items-center justify-center flex-none min-w-0">
              {renderHelpPill()}
            </div>

            <div className="flex-1 flex justify-end">
              {isMetadataNoHelp && (
                <button
                  type="button"
                  onClick={() => setShowExerciseHelp(true)}
                  className="h-10 px-4 rounded-full bg-white/70 hover:bg-white border border-indigo-200/70 shadow-[0_10px_30px_-22px_rgba(99,102,241,.8)] hover:shadow-[0_18px_50px_-28px_rgba(99,102,241,1)] transition-all duration-200 text-sm font-bold text-indigo-700 inline-flex items-center gap-2 hover:-translate-y-[1px]"
                  title="About this course"
                >
                  <span className="text-base">✨</span>
                  <span>About this course</span>
                  <span className="opacity-70">›</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handlePreviousSegment}
              disabled={segment.id === 0}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                segment.id === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-lg leading-none">←</span>
              <span className="text-sm font-medium">Previous</span>
            </button>

            <div className="flex-1 min-w-0 h-10 flex items-center justify-center">
              {!showCenterCue ? (
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {sentenceLabel}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-semibold transition-all duration-200 ${
                      practiceMode === "shadowing" &&
                      recordingStatus === "recording"
                        ? "text-green-600 scale-105"
                        : "text-gray-700"
                    }`}
                  >
                    {practiceMode === "shadowing" &&
                    recordingStatus === "recording"
                      ? "Go!"
                      : "Get ready…"}
                  </span>

                  {practiceMode === "shadowing" &&
                    isCueActive &&
                    recordingStatus !== "recording" && (
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 1 ? "bg-amber-500" : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 2 ? "bg-orange-500" : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            cuePulseCount >= 3
                              ? "bg-green-500 animate-pulse"
                              : "bg-gray-300"
                          }`}
                        />
                      </div>
                    )}

                  {practiceMode === "echoing" &&
                    echoingPhase === "paused" && (
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 1
                              ? "bg-blue-500"
                              : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 2
                              ? "bg-indigo-500"
                              : "bg-gray-300"
                          }`}
                        />
                        <div className="w-3 h-0.5 bg-gray-300" />
                        <div
                          className={`w-2 h-2 rounded-full ${
                            echoingPulseCount >= 3
                              ? "bg-green-500 animate-pulse"
                              : "bg-gray-300"
                          }`}
                        />
                      </div>
                    )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleNextSegment}
              disabled={segment.id >= segments.length - 1}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                segment.id >= segments.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
              ].join(" ")}
            >
              <span className="text-sm font-medium">Next</span>
              <span className="text-lg leading-none">→</span>
            </button>
          </div>
        </div>
      </div>

      <div
        ref={selectionAreaRef}
        className="relative min-h-[96px] pt-14 px-4 pb-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-2xl border border-blue-200/30 overflow-visible"
      >
      {showSelectionMenu && normalizedSelection && practiceMode !== "shadowing" && (
  <div className="absolute left-1/2 top-3 -translate-x-1/2 z-50 pointer-events-none">
    <div className="pointer-events-auto flex items-center gap-2 px-2 py-2 rounded-2xl bg-slate-900/95 text-white shadow-2xl border border-slate-700">
      <button
        type="button"
        onClick={playSelectedRange}
        className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium transition whitespace-nowrap"
      >
        ▶ Play selected
      </button>

     <button
  type="button"
  onClick={clearSelection}
  className="px-3 py-1.5 rounded-xl bg-gray  text-gray-700 hover:bg-slate-100 text-sm font-medium transition whitespace-nowrap border border-slate-300 shadow-sm"
>
  Clear
</button>
    </div>
  </div>
)}
        <div className="flex flex-wrap gap-2 justify-center items-center">
          {segment?.words?.map((item, i) => {
            const isActive = currentWord === i;

            const isShadowCueWord =
              practiceMode === "shadowing" &&
              isCueActive &&
              cuePulseCount >= 1 &&
              i === 0;

            const isInSelection =
              normalizedSelection &&
              i >= normalizedSelection.start &&
              i <= normalizedSelection.end;

            const isMultiSelection =
              normalizedSelection &&
              normalizedSelection.start !== normalizedSelection.end;

            const onWordClick =
              practiceMode === "shadowing"
                ? () => handleWordPlay(i, item)
                : (e) => {
                    if (isMultiSelection) return;
                    openWordMenu(e, item.text, i);
                  };

            const activeClass =
              practiceMode === "shadowing"
                ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25 transform scale-105"
                : practiceMode === "echoing"
                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25 transform scale-105"
                : "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/25 transform scale-105";

            const idleClass =
              "text-gray-800 bg-white/80 hover:bg-white border border-white/40 hover:border-white/60 hover:shadow-md";

           const selectionClass =
  "bg-gradient-to-r from-green-300 to-green-300 text-white font-semibold border border-green-500 shadow-lg shadow-green-300/30 scale-105";
            const ringClass =
              practiceMode === "manual"
                ? "ring-1 ring-emerald-200/60"
                : practiceMode === "echoing"
                ? "ring-1 ring-indigo-200/60"
                : "ring-1 ring-orange-200/60";

            return (
              <span
                key={i}
                onClick={onWordClick}
                onMouseDown={(e) => handleWordMouseDown(i, e)}
                onMouseEnter={() => handleWordMouseEnter(i)}
                onMouseUp={handleWordMouseUp}
                className={[
                  "text-lg font-medium px-3 py-2 rounded-xl transition-all duration-300 cursor-pointer relative overflow-hidden select-none",
                  ringClass,
                  isInSelection ? selectionClass : isActive ? activeClass : idleClass,
                ].join(" ")}
                title={
                  practiceMode === "shadowing"
                    ? "Tap to play from here"
                    : "Drag to select words"
                }
              >
                {item.text}

                {isShadowCueWord && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-xl" />
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SentencePracticeBlock;